import { Link } from 'react-router-dom';
import { DocSection, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'overview', label: 'Overview' },
  { id: 'plan-first', label: 'The plan-first workflow' },
  { id: 'beyond', label: 'Same as you, and beyond' },
  { id: 'what-agent-sees', label: 'What the agent receives' },
  { id: 'skills', label: 'Bundled workflow skills' },
  { id: 'privacy', label: 'On privacy' },
];

export default function Body() {
  return (
    <>
      <DocSection id="overview" title="Overview">
        <p>
          ClipRadar ships a local <strong>Agent (MCP)</strong> server — Model Context Protocol, the
          programmatic surface for driving ClipRadar from an AI agent like Claude Code, Claude
          Desktop, or Codex. There's no public REST API; MCP is how an agent searches your library,
          verifies what it found with a thumbnail, exports clips or a timeline, and — when the
          Premiere panel is open — builds directly inside your Premiere project.
        </p>
        <DocShot
          slug="agentic-editing"
          shot="agent-settings"
          alt="Settings → Agent (MCP) status and connect command"
          caption="Settings → Agent (MCP): live connection status and a one-time connect command for your agent."
          callouts={[
            { n: 1, label: 'Status — Connected, Connectable, or unreachable, updated live' },
            { n: 2, label: 'Connect command — copy the one-time setup command for your agent' },
          ]}
        />
      </DocSection>

      <DocSection id="plan-first" title="The plan-first workflow">
        <p>
          An agent connected to ClipRadar doesn't jump straight to acting. It's built around a
          mandatory sequence: <strong>discover</strong> what's in your library,{' '}
          <strong>read the matching workflow skill</strong> for the task, <strong>present a short
          plan</strong> in the chat, and only then <strong>act</strong> once you approve it. Ask it
          to "find the drone shots and cut them together" and you get a plan first — which sources
          it'll search, what it'll export or place — not a finished timeline you didn't ask to see
          yet.
        </p>
      </DocSection>

      <DocSection id="beyond" title="Same as you, and beyond">
        <p>
          An agent can do everything you can from the Search tab — visual, speech, and people
          search — across your entire library in one turn, instead of one query at a time. And when
          the ClipRadar panel is open inside Premiere, it goes further than clicking around
          normally can: it can build a whole new sequence directly in your open project, insert or
          place individual clips, jump the Source Monitor to a moment, and mark in/out points — the
          same live timeline control the panel gives you, driven by conversation instead of clicks.
          Without the panel open, the agent exports a timeline XML you import yourself — see{' '}
          <Link to="/docs/guides/build-a-rough-cut">From search results to a rough cut</Link>.
        </p>
      </DocSection>

      <DocSection id="what-agent-sees" title="What the agent receives">
        <p>
          Search and verification tools hand the agent <strong>metadata and thumbnails</strong>,
          not your footage — a small JPEG to confirm a hit is the right one, timestamps, transcript
          text, filenames. Exports are sandboxed to a dedicated agent workspace folder plus your
          normal <Link to="/docs/interface/settings">Export folder</Link>, so an agent can't write
          files anywhere else on your machine. The full list of what each tool returns is in the{' '}
          <Link to="/docs/reference/mcp-tools">MCP tool reference</Link>.
        </p>
      </DocSection>

      <DocSection id="skills" title="Bundled workflow skills">
        <p>
          ClipRadar ships a set of workflow skills the agent reads before acting, so it follows a
          proven strategy instead of improvising: a voiceover-and-B-roll assembly, a highlight reel,
          hunting for quotable soundbites across interviews, cross-referencing who said what, and
          more. They're bundled with the MCP server and also installable as standalone{' '}
          <span style={kbd}>clipradar-*</span> agent skills if your tool supports them.
        </p>
      </DocSection>

      <DocSection id="privacy" title="On privacy">
        <p>
          The MCP server itself is a local process — it never sends anything anywhere on its own.
          What happens after it hands data to your agent depends on that agent: if it's backed by a
          cloud model, as most AI coding assistants are, the metadata and thumbnails ClipRadar
          returns become part of that conversation, the same as any other tool result you'd share
          with it. Your actual footage is never part of that exchange — MCP tools return thumbnails
          and text, never video files. See{' '}
          <Link to="/docs/concepts/local-and-private">Local &amp; private</Link> for the full
          picture of what does and doesn't leave your machine.
        </p>
      </DocSection>
    </>
  );
}
